import React, { useEffect, useState } from 'react'
import { NumericFormat } from 'react-number-format'

import Checkbox from '../../../../../../components/form/Checkbox'

import MoneyInput from '../../../../../../components/form/MoneyInput'
import LoadingIcon from '../../../../../../components/LoadingIcon'
import { CurrencyList, ContractPosition } from '../../../types'

import { Okei } from '../../../../Plans/Positions/Edit/types'

import {
    PerformanceContractDocPositionDraft,
    ContractPerformancePayment,
    ContractPerformancePaymentAcceptance,
    ContractPerformancePaymentInit,
    ContractPerformancePaymentAcceptanceSet,
} from '../types'

import HoverTooltip from '../../../../../../components/HoverTooltip'
import PerformancePaymentsAcceptancePopupTableRow from './PerformanceInfoTable/PerformancePaymentsAcceptancePopupTableRow'
import PerformancePaymentsAcceptancePopup from './PerformancePaymentsAcceptancePopup'

type Props = {
    theme: string
    performanceGuid: string | undefined
    currencyList: CurrencyList[]
    handlePerformancePaymentsAddPopup: () => void
    performanceContractPaymentsPosition: ContractPerformancePaymentInit
    contractGuid: string | undefined
    contractRegNumber: string | undefined
    isLoadingPerformancePaymentsPosition: boolean
    onPrepaid: (pay_prepaid: boolean) => void
    onPayNumber: (pay_number: string) => void
    onPayDate: (pay_date: string) => void
    onPayPrice: (pay_price: number) => void
    onPayRubPrice: (pay_rub_price: number) => void
    onChangeCurrency: (currency: {
        code: string
        name: string
        digital_code: number
    }) => void
    onChangeCurrencyRate: (currency_rate: number | null) => void
    isSavingPerformancePay: boolean
    onAddPay: (data: ContractPerformancePaymentInit) => void
    onChangeAcceptances: (
        acceptances: ContractPerformancePaymentAcceptanceSet[]
    ) => void
}

interface CodeItem {
    code: string
    name: string
}

const PerformancePaymentsAddPopup: React.FC<Props> = (props) => {
    const [selectedItems, setSelectedItems] = useState<number[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    const [okeiList, setOkeiList] = useState<Okei[]>([])

    // Локальные значения для инпутов (строки с запятой)
    const [inputValuePrice, setInputValuePrice] = useState('')
    const [inputValuePriceRub, setInputValuePriceRub] = useState('')
    const [inputValueCurrencyRate, setInputValueCurrencyRate] = useState<
        string | null
    >('')

    const [
        performancePaymentsAcceptancePopup,
        setPerformancePaymentsAcceptancePopup,
    ] = useState<boolean>(false)

    const [
        deleteRowPerformancePaymentAcceptancePopup,
        setDeleteRowPerformancePaymentAcceptancePopup,
    ] = useState<boolean>(false)

    const [acceptancesTouched, setAcceptancesTouched] = useState(false)

    const getOkeiList = async (): Promise<Okei[]> => {
        return fetch(process.env.REACT_APP_API_URL + '/api/backend/v1/okei', {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на способы закупок')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    // безопасный парсер десятичных (точка) из строки/числа/строки с запятой
    const parseDec = (v: string | number | null | undefined) => {
        if (v === null || v === undefined) return undefined
        if (typeof v === 'number') return v
        const num = Number(String(v).replace(',', '.'))
        return Number.isNaN(num) ? undefined : num
    }

    const toNumber = (v: number | string | null | undefined): number => {
        if (v === null || v === undefined || v === '') return 0
        const num =
            typeof v === 'number' ? v : Number(String(v).replace(',', '.'))
        return Number.isNaN(num) ? 0 : num
    }

    const formatDecimalInput = (value: number): string => {
        if (!Number.isFinite(value)) return ''

        return value.toFixed(2).replace('.', ',')
    }

    const getAcceptanceNativeSum = (
        acceptance: ContractPerformancePaymentAcceptanceSet
    ): number => {
        if (acceptance.qty !== null && acceptance.unit_price !== null) {
            return toNumber(acceptance.qty) * toNumber(acceptance.unit_price)
        }

        return 0
    }

    const getAcceptanceRubSum = (
        acceptance: ContractPerformancePaymentAcceptanceSet
    ): number => {
        if (
            acceptance.total_rub !== null &&
            acceptance.total_rub !== undefined
        ) {
            return toNumber(acceptance.total_rub)
        }

        if (
            acceptance.qty !== null &&
            acceptance.rub_unit_price !== null &&
            acceptance.rub_unit_price !== undefined
        ) {
            return (
                toNumber(acceptance.qty) * toNumber(acceptance.rub_unit_price)
            )
        }

        if (acceptance.currency_digital_code === 643) {
            return getAcceptanceNativeSum(acceptance)
        }

        return (
            getAcceptanceNativeSum(acceptance) *
            toNumber(acceptance.exchange_rate)
        )
    }

    const calcAcceptancesSums = (
        acceptances: ContractPerformancePaymentAcceptanceSet[]
    ) => {
        const nativeSum = acceptances.reduce(
            (sum, item) => sum + getAcceptanceNativeSum(item),
            0
        )

        const rubSum = acceptances.reduce(
            (sum, item) => sum + getAcceptanceRubSum(item),
            0
        )

        return {
            nativeSum,
            rubSum,
        }
    }

    // Функция для обработки изменения состояния отдельного checkbox
    const handleCheckboxChange = (index: number) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(index)) {
                return prevSelectedItems.filter((i) => i !== index)
            } else {
                return [...prevSelectedItems, index]
            }
        })
        setSelectAll(false)
    }

    // Функция для обработки изменения состояния отдельного checkbox при нажатии правой кнопкой
    const handleRightClickCheckboxChange = (index: number) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(index)) {
                return prevSelectedItems // уже выбран — ничего не меняем
            }
            return [...prevSelectedItems, index]
        })
        setSelectAll(false)
    }

    // Функция для обработки изменения состояния "Select All"
    const handleSelectAllChange = () => {
        setSelectAll((prev) => !prev)
        if (!selectAll) {
            const items =
                props.performanceContractPaymentsPosition.acceptances ?? []
            const allIndexes = items.map((_, idx) => idx)
            setSelectedItems(allIndexes)
        } else {
            setSelectedItems([])
        }
    }

    const handleDeleteRowPerformanceDocsPositionPopup = () => {
        setDeleteRowPerformancePaymentAcceptancePopup(
            !deleteRowPerformancePaymentAcceptancePopup
        )
    }

    useEffect(() => {
        const fetchData = async () => {
            const okei = await getOkeiList()
            if (okei) {
                setOkeiList(okei)
            }
        }
        fetchData()
    }, [])

    // Синхронизируем локальный инпут суммы с props (при открытии/обновлении)
    useEffect(() => {
        if (
            props.performanceContractPaymentsPosition.payment.price !==
                undefined &&
            props.performanceContractPaymentsPosition.payment.price !== null
        ) {
            setInputValuePrice(
                props.performanceContractPaymentsPosition.payment.price
                    .toString()
                    .replace(/\./g, ',')
            )
        } else {
            setInputValuePrice('')
        }
    }, [props.performanceContractPaymentsPosition.payment.price])

    // Курс валюты
    useEffect(() => {
        if (props.performanceContractPaymentsPosition.payment.currency_rate) {
            setInputValueCurrencyRate(
                props.performanceContractPaymentsPosition.payment.currency_rate
                    .toString()
                    .replace(/\./g, ',')
            )
        } else setInputValueCurrencyRate('')
    }, [props.performanceContractPaymentsPosition.payment.currency_rate])

    // Сумма в рублях
    useEffect(() => {
        if (props.performanceContractPaymentsPosition.payment.rub_price) {
            setInputValuePriceRub(
                props.performanceContractPaymentsPosition.payment.rub_price
                    .toString()
                    .replace(/\./g, ',')
            )
        } else setInputValuePriceRub('')
    }, [props.performanceContractPaymentsPosition.payment.rub_price])

    const handlePerformancePaymentsAcceptancePopup = () => {
        setPerformancePaymentsAcceptancePopup(
            !performancePaymentsAcceptancePopup
        )
    }

    const updateAcceptancesWithPaymentSum = (
        acceptances: ContractPerformancePaymentAcceptanceSet[]
    ) => {
        const payment = props.performanceContractPaymentsPosition.payment
        const { nativeSum, rubSum } = calcAcceptancesSums(acceptances)

        const isRubPayment = payment.currency_digital_code === 643

        const nextPrice = isRubPayment ? rubSum : nativeSum

        props.onChangeAcceptances(acceptances)

        props.onPayPrice(nextPrice)
        setInputValuePrice(formatDecimalInput(nextPrice))

        if (isRubPayment) {
            props.onPayRubPrice(nextPrice)
            setInputValuePriceRub(formatDecimalInput(nextPrice))
            return
        }

        const currentRate = parseDec(inputValueCurrencyRate)

        if (currentRate !== undefined && currentRate > 0) {
            const calculatedRub = nextPrice * currentRate
            props.onPayRubPrice(calculatedRub)
            setInputValuePriceRub(formatDecimalInput(calculatedRub))
            return
        }

        props.onPayRubPrice(rubSum)
        setInputValuePriceRub(formatDecimalInput(rubSum))
    }

    const handleDeleteSelected = () => {
        const current = props.performanceContractPaymentsPosition.acceptances

        const remaining = current.filter(
            (_, idx) => !selectedItems.includes(idx)
        )

        updateAcceptancesWithPaymentSum(remaining)

        setSelectedItems([])
        setSelectAll(false)
    }

    // ВАЖНО: перед сохранением забираем актуальные значения из локальных инпутов
    const handleSave = () => {
        const payment = props.performanceContractPaymentsPosition.payment

        const price = parseDec(inputValuePrice) ?? 0

        // currency_rate: number | null
        let currency_rate: number | null = null
        if (payment.currency_digital_code !== 643) {
            currency_rate = parseDec(inputValueCurrencyRate) ?? 0
        } else {
            currency_rate = null
        }

        const rub_price =
            payment.currency_digital_code !== 643
                ? price * (currency_rate ?? 0) // для нерублевой валюты
                : price // для 643 просто цена

        const payload: ContractPerformancePaymentInit = {
            ...props.performanceContractPaymentsPosition,
            payment: {
                ...payment,
                price,
                currency_rate, // <- тут уже точно null для 643
                rub_price,
            },
        }

        props.onAddPay(payload)
        props.handlePerformancePaymentsAddPopup()
    }

    const payment = props.performanceContractPaymentsPosition.payment

    const hasPayNumber =
        typeof payment?.annotation === 'string' &&
        payment.annotation.trim().length > 0

    const hasPayDate =
        typeof payment?.payment_date === 'string' &&
        payment.payment_date.trim().length > 0

    // Сумма оплаты — проверяем, что поле не пустое и число валидное
    const rawPrice = inputValuePrice.trim()
    const priceNum = rawPrice !== '' ? parseDec(rawPrice) : undefined
    const hasPrice = rawPrice !== '' && priceNum !== undefined

    const hasCurrency =
        payment?.currency_digital_code !== null &&
        payment?.currency_digital_code !== undefined

    const isRub = payment?.currency_digital_code === 643

    // Курс валюты (только для не-рублевой валюты)
    const rawRate = (inputValueCurrencyRate ?? '').trim()
    const rateNum = rawRate !== '' ? parseDec(rawRate) : undefined
    const hasCurrencyRate = rawRate !== '' && rateNum !== undefined

    // Сумма в рублях (readOnly, но проверяем, что не пустая и число валидное)
    const rawRub = inputValuePriceRub.trim()
    const rubNum = rawRub !== '' ? parseDec(rawRub) : undefined
    const hasRubSum = rawRub !== '' && rubNum !== undefined

    const canSave =
        hasPayNumber &&
        hasPayDate &&
        hasPrice &&
        hasCurrency &&
        (isRub || (hasCurrencyRate && hasRubSum))

    const isSaveDisabled = !canSave || props.isSavingPerformancePay

    const linkedDocumentsRubSum = calcAcceptancesSums(
        props.performanceContractPaymentsPosition.acceptances ?? []
    ).rubSum

    return (
        <div className="fixed z-10 top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center">
            <div
                className="flex flex-col p-5 rounded-lg w-[1300px] h-fit gap-y-5 bg-[#e5e5e5] dark:bg-[#262427]"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="text-[#181818] dark:text-[#FFFFFF]">
                    Добавление оплаты
                </h2>

                <div className="flex flex-col w-full gap-3">
                    <div className="flex flex-col w-[200px]">
                        <Checkbox
                            label="Оплата авансом"
                            checked={
                                props.performanceContractPaymentsPosition
                                    ?.payment?.prepaid
                                    ? props.performanceContractPaymentsPosition
                                          .payment.prepaid
                                    : false
                            }
                            onChange={props.onPrepaid}
                            disabled={false}
                        />
                    </div>

                    <div className="flex flex-row w-full h-fit gap-3 items-center justify-start">
                        <div className="flex flex-col w-[200px]">
                            <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Номер платежки
                            </label>
                            <input
                                type="text"
                                className={`form-input user-input-placeholder w-full`}
                                value={
                                    props.performanceContractPaymentsPosition
                                        ?.payment?.annotation != null
                                        ? props
                                              .performanceContractPaymentsPosition
                                              .payment.annotation
                                        : ''
                                }
                                onChange={(e) =>
                                    props.onPayNumber(e.target.value)
                                }
                            />
                        </div>
                        <div className="flex flex-col w-[200px]">
                            <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Дата оплаты
                            </label>
                            <input
                                type="date"
                                style={{
                                    colorScheme:
                                        props.theme == 'dark'
                                            ? 'dark'
                                            : 'light',
                                }}
                                className="form-input w-full bg-[#1F1E1F]"
                                value={
                                    props.performanceContractPaymentsPosition
                                        ?.payment?.payment_date != null
                                        ? props
                                              .performanceContractPaymentsPosition
                                              .payment.payment_date
                                        : ''
                                }
                                onChange={(e) =>
                                    props.onPayDate(e.target.value)
                                }
                            />
                        </div>
                        {/* Сумма оплаты */}
                        <div className="flex flex-col w-[200px]">
                            <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Сумма оплаты
                            </label>
                            <input
                                type="text"
                                inputMode="decimal"
                                className="form-input user-input-placeholder w-full"
                                value={inputValuePrice}
                                onChange={(e) => {
                                    let value = e.target.value

                                    // 1. Заменяем точку на запятую сразу
                                    value = value.replace(/\./g, ',')

                                    // 2. Разрешаем только цифры и запятую
                                    value = value.replace(/[^0-9,]/g, '')

                                    // 3. Разрешаем только одну запятую
                                    const parts = value.split(',')
                                    if (parts.length > 2) {
                                        value =
                                            parts[0] +
                                            ',' +
                                            parts.slice(1).join('')
                                    }

                                    setInputValuePrice(value)
                                }}
                                onBlur={() => {
                                    const raw = inputValuePrice.trim()

                                    if (raw === '') {
                                        props.onPayPrice(0)
                                        props.onPayRubPrice(0)
                                        setInputValuePriceRub('')
                                        return
                                    }

                                    const num = parseDec(raw)
                                    if (num === undefined) {
                                        props.onPayPrice(0)
                                        props.onPayRubPrice(0)
                                        setInputValuePrice('')
                                        setInputValuePriceRub('')
                                        return
                                    }

                                    props.onPayPrice(num)

                                    if (
                                        props
                                            .performanceContractPaymentsPosition
                                            .payment.currency_digital_code !==
                                        643
                                    ) {
                                        const rate =
                                            parseDec(inputValueCurrencyRate) ??
                                            0
                                        const rub = num * rate
                                        props.onPayRubPrice(rub)
                                        setInputValuePriceRub(
                                            rub.toString().replace(/\./g, ',')
                                        )
                                    } else {
                                        props.onPayRubPrice(num)
                                        setInputValuePriceRub(
                                            num.toString().replace(/\./g, ',')
                                        )
                                    }
                                }}
                            />
                        </div>
                        {/* Валюта */}
                        <div className="flex flex-col w-[200px]">
                            <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Валюта
                            </label>
                            <select
                                className="form-select px-2 rounded-md w-full h-[28px]"
                                value={
                                    props.performanceContractPaymentsPosition
                                        ?.payment?.currency_digital_code != null
                                        ? props
                                              .performanceContractPaymentsPosition
                                              .payment.currency_digital_code
                                        : 643
                                }
                                onChange={(e) => {
                                    const selectedCode = e.target.value
                                    const selectedItem =
                                        props.currencyList.find(
                                            (item) =>
                                                item.num_code === selectedCode
                                        )
                                    if (selectedItem) {
                                        props.onChangeCurrency({
                                            code: selectedItem.char_code,
                                            name: selectedItem.name,
                                            digital_code: Number(
                                                selectedItem.num_code
                                            ),
                                        })
                                    }

                                    const newDigitalCode = Number(selectedCode)

                                    if (newDigitalCode === 643) {
                                        // рубли
                                        const price =
                                            parseDec(inputValuePrice) ?? 0
                                        props.onChangeCurrencyRate(null)
                                        props.onPayRubPrice(price)
                                        setInputValueCurrencyRate(null)
                                        setInputValuePriceRub(
                                            price.toString().replace(/\./g, ',')
                                        )
                                    } else {
                                        // иностранная валюта
                                        const price =
                                            parseDec(inputValuePrice) ?? 0
                                        const rate =
                                            parseDec(inputValueCurrencyRate) ??
                                            0
                                        const rub = price * rate
                                        props.onChangeCurrencyRate(rate)
                                        props.onPayRubPrice(rub)
                                        setInputValuePriceRub(
                                            rub.toString().replace(/\./g, ',')
                                        )
                                    }
                                }}
                            >
                                <option value="">Выбрать</option>
                                {props.currencyList.map((curr, index) => (
                                    <option key={index} value={curr.num_code}>
                                        {curr.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {/* Курс и сумма в рублях, если валюта не 643 */}
                        {props.performanceContractPaymentsPosition?.payment
                            ?.currency_digital_code != null &&
                        props.performanceContractPaymentsPosition.payment
                            .currency_digital_code != 643 ? (
                            <>
                                <div className="flex flex-col w-[200px]">
                                    <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                        Курс валюты
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-input user-input-placeholder w-full`}
                                        value={inputValueCurrencyRate ?? 0}
                                        onChange={(e) => {
                                            let value = e.target.value

                                            value = value.replace(/\./g, ',')
                                            value = value.replace(
                                                /[^0-9,]/g,
                                                ''
                                            )

                                            const parts = value.split(',')
                                            if (parts.length > 2) {
                                                value =
                                                    parts[0] +
                                                    ',' +
                                                    parts.slice(1).join('')
                                            }

                                            setInputValueCurrencyRate(value)
                                        }}
                                        onBlur={() => {
                                            const raw =
                                                inputValueCurrencyRate?.trim()

                                            if (raw === '') {
                                                props.onChangeCurrencyRate(null)
                                                props.onPayRubPrice(0)
                                                setInputValuePriceRub('')
                                                return
                                            }

                                            const rate = parseDec(raw)
                                            if (rate === undefined) {
                                                props.onChangeCurrencyRate(null)
                                                props.onPayRubPrice(0)
                                                setInputValueCurrencyRate('')
                                                setInputValuePriceRub('')
                                                return
                                            }

                                            props.onChangeCurrencyRate(rate)

                                            const price =
                                                parseDec(inputValuePrice) ?? 0
                                            const rub = price * rate
                                            props.onPayRubPrice(rub)
                                            setInputValuePriceRub(
                                                rub
                                                    .toString()
                                                    .replace(/\./g, ',')
                                            )
                                        }}
                                    />
                                </div>
                                <div className="flex flex-col w-[200px]">
                                    <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                        Сумма в руб.
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-input user-input-placeholder w-full`}
                                        readOnly
                                        disabled
                                        value={inputValuePriceRub}
                                    />
                                </div>
                            </>
                        ) : null}
                    </div>
                </div>

                {/* Таблица привязанных документов */}
                <div className="flex flex-col w-full h-fit items-center justify-center bg-[#e4e6e8] dark:bg-[#242225] rounded">
                    <div className="flex flex-row gap-1 p-1 w-full h-[40px] text-[11px] rounded-t text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033] items-center justify-center">
                        <div className="flex w-8 h-full items-center justify-center text-center">
                            <div className="flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]">
                                <div
                                    className="w-6 h-6 grid rounded-full place-items-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={handleSelectAllChange}
                                >
                                    <HoverTooltip tooltipText="Выбрать все">
                                        <input
                                            type="checkbox"
                                            className="p-2"
                                            checked={selectAll}
                                            readOnly
                                        />
                                    </HoverTooltip>
                                </div>
                            </div>
                        </div>
                        <div className="flex w-8 h-full items-center justify-center text-center">
                            <button
                                className="w-6 h-6 grid rounded-full place-items-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                onClick={
                                    handlePerformancePaymentsAcceptancePopup
                                }
                            >
                                <HoverTooltip tooltipText="Связать с документом">
                                    {/* icon */}
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="plus"
                                    >
                                        <path
                                            fill={
                                                props.theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                            }
                                            d="M10,17.55,8.23,19.27a2.47,2.47,0,0,1-3.5-3.5l4.54-4.55a2.46,2.46,0,0,1,3.39-.09l.12.1a1,1,0,0,0,1.4-1.43A2.75,2.75,0,0,0,14,9.59a4.46,4.46,0,0,0-6.09.22L3.31,14.36a4.48,4.48,0,0,0,6.33,6.33L11.37,19A1,1,0,0,0,10,17.55ZM20.69,3.31a4.49,4.49,0,0,0-6.33,0L12.63,5A1,1,0,0,0,14,6.45l1.73-1.72a2.47,2.47,0,0,1,3.5,3.5l-4.54,4.55a2.46,2.46,0,0,1-3.39.09l-.12-.1a1,1,0,0,0-1.4,1.43,2.75,2.75,0,0,0,.23.21,4.47,4.47,0,0,0,6.09-.22l4.55-4.55A4.49,4.49,0,0,0,20.69,3.31Z"
                                        ></path>
                                    </svg>
                                </HoverTooltip>
                            </button>
                        </div>
                        <div className="flex w-8 h-full items-center justify-center text-center">
                            <button
                                className={`w-6 h-6 grid rounded-full place-items-center ${selectedItems.length > 0 ? 'hover:bg-[#ed9cae] dark:hover:bg-[#701f1f]' : ''}`}
                                onClick={
                                    handleDeleteRowPerformanceDocsPositionPopup
                                }
                                disabled={!(selectedItems.length > 0)}
                            >
                                <HoverTooltip tooltipText="Отвязать документ">
                                    {/* icon */}
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="plus"
                                    >
                                        <path
                                            fill={
                                                selectedItems.length > 0
                                                    ? props.theme == 'dark'
                                                        ? '#FFFFFF'
                                                        : '#181818'
                                                    : '#8A8A8A'
                                            }
                                            d="M4.76,10.59a1,1,0,0,0,.26-2L3.26,8.15a1,1,0,1,0-.52,1.93l1.76.47A.78.78,0,0,0,4.76,10.59ZM8.62,5a1,1,0,0,0,1,.74.82.82,0,0,0,.26,0,1,1,0,0,0,.7-1.22l-.47-1.76a1,1,0,1,0-1.93.52Zm4.83,10A1,1,0,0,0,12,15L8.5,18.56a2.21,2.21,0,0,1-3.06,0,2.15,2.15,0,0,1,0-3.06L9,12a1,1,0,1,0-1.41-1.41L4,14.08A4.17,4.17,0,1,0,9.92,20l3.53-3.53A1,1,0,0,0,13.45,15ZM5.18,6.59a1,1,0,0,0,.7.29,1,1,0,0,0,.71-.29,1,1,0,0,0,0-1.41L5.3,3.89A1,1,0,0,0,3.89,5.3Zm16.08,7.33-1.76-.47A1,1,0,1,0,19,15.38l1.76.47.26,0a1,1,0,0,0,.26-2ZM15.38,19a1,1,0,0,0-1.23-.7,1,1,0,0,0-.7,1.22l.47,1.76a1,1,0,0,0,1,.74,1.15,1.15,0,0,0,.26,0,1,1,0,0,0,.71-1.23Zm3.44-1.57a1,1,0,0,0-1.41,1.41l1.29,1.29a1,1,0,0,0,1.41,0,1,1,0,0,0,0-1.41ZM21.2,7A4.16,4.16,0,0,0,14.08,4L10.55,7.56A1,1,0,1,0,12,9L15.5,5.44a2.21,2.21,0,0,1,3.06,0,2.15,2.15,0,0,1,0,3.06L15,12a1,1,0,0,0,0,1.41,1,1,0,0,0,1.41,0L20,9.92A4.19,4.19,0,0,0,21.2,7Z"
                                        ></path>
                                    </svg>
                                </HoverTooltip>
                            </button>
                        </div>
                        <div className="flex flex-1 w-full h-full items-center justify-center text-center"></div>
                        <div className="flex flex-col w-[215px] h-full pr-3 gap-1 items-center justify-center text-center">
                            <div className="flex w-full h-fit items-center justify-end text-right">
                                Сумма всего:
                                <br />
                                {linkedDocumentsRubSum.toLocaleString('ru-RU', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row gap-1 w-full h-[40px] p-1 px-2 items-center justify-center text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#2a282b]">
                        <div className="flex w-4 h-full items-center justify-center text-center"></div>
                        <div className="flex w-10 h-full items-center justify-center text-center">
                            №
                        </div>
                        <div className="flex flex-1 items-center justify-center ">
                            Наименование документа
                        </div>
                        <div className="flex w-[190px] items-center justify-center text-center">
                            Дата
                        </div>
                        <div className="flex flex-1 items-center justify-center ">
                            Наменование ТРУ
                        </div>
                        <div className="flex w-32 items-center justify-center text-center">
                            Единица измерения
                        </div>
                        <div className="flex w-32 items-center justify-center text-center">
                            Количество (объем)
                        </div>
                        <div className="flex w-32 items-center justify-center text-center">
                            Цена за единицу
                        </div>
                        <div className="flex w-32 items-center justify-center text-center">
                            Сумма по позиции
                        </div>
                    </div>
                    <div className="flex flex-col w-full h-[400px] gap-1 py-1 overflow-y-auto custom-scrollbar">
                        {props.isLoadingPerformancePaymentsPosition ? (
                            <div className="flex w-full h-full items-center justify-center">
                                <LoadingIcon />
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1 text-[11px] items-start justify-center">
                                {props.performanceContractPaymentsPosition
                                    ?.acceptances
                                    ? props.performanceContractPaymentsPosition.acceptances.map(
                                          (row, index) => (
                                              <PerformancePaymentsAcceptancePopupTableRow
                                                  key={index}
                                                  index={index + 1}
                                                  theme={props.theme}
                                                  performanceContractPaymentsPosition={
                                                      props.performanceContractPaymentsPosition
                                                  }
                                                  performanceContractPaymentsAcceptance={
                                                      row
                                                  }
                                                  selected={selectedItems.includes(
                                                      index
                                                  )}
                                                  onClick={() => {
                                                      handleCheckboxChange(
                                                          index
                                                      )
                                                  }}
                                                  onRightClick={() => {
                                                      handleRightClickCheckboxChange(
                                                          index
                                                      )
                                                  }}
                                              />
                                          )
                                      )
                                    : ''}
                            </div>
                        )}
                    </div>
                    {/* buttons */}
                    <div className="flex flex-row gap-1 w-full h-[40px] text-[10px] p-1 rounded-b text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#333033]">
                        <div className="flex flex-1 flex-row gap-3 px-5 h-full items-center justify-start text-center">
                            <button
                                className="flex flex-row gap-2 px-4 py-1 min-w-[120px] items-center justify-center rounded-[10px] text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                                disabled={false}
                                onClick={
                                    handlePerformancePaymentsAcceptancePopup
                                }
                            >
                                <svg
                                    className="w-3 h-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 22 22"
                                    id="plus"
                                >
                                    <path
                                        fill={`${props.theme == 'dark' ? '#FFFFFF' : '#181818'}`}
                                        d="M10,17.55,8.23,19.27a2.47,2.47,0,0,1-3.5-3.5l4.54-4.55a2.46,2.46,0,0,1,3.39-.09l.12.1a1,1,0,0,0,1.4-1.43A2.75,2.75,0,0,0,14,9.59a4.46,4.46,0,0,0-6.09.22L3.31,14.36a4.48,4.48,0,0,0,6.33,6.33L11.37,19A1,1,0,0,0,10,17.55ZM20.69,3.31a4.49,4.49,0,0,0-6.33,0L12.63,5A1,1,0,0,0,14,6.45l1.73-1.72a2.47,2.47,0,0,1,3.5,3.5l-4.54,4.55a2.46,2.46,0,0,1-3.39.09l-.12-.1a1,1,0,0,0-1.4,1.43,2.75,2.75,0,0,0,.23.21,4.47,4.47,0,0,0,6.09-.22l4.55-4.55A4.49,4.49,0,0,0,20.69,3.31Z"
                                    ></path>
                                </svg>
                                Связать с документом
                            </button>
                            <button
                                className={`flex flex-row gap-2 px-4 py-1 min-w-[120px] items-center justify-center rounded-[10px] text-xs ${selectedItems.length > 0 ? 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] hover:bg-[#ed9cae] dark:hover:bg-[#701f1f] bg-[#f6f6f6] dark:bg-[#494849]' : 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#f6f6f6] dark:bg-[#494849]'} `}
                                disabled={!(selectedItems.length > 0)}
                                onClick={
                                    handleDeleteRowPerformanceDocsPositionPopup
                                }
                            >
                                <svg
                                    className="w-3 h-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 22 22"
                                    id="trash"
                                >
                                    <path
                                        fill={
                                            selectedItems.length > 0
                                                ? props.theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                                : props.theme == 'dark'
                                                  ? '#8A8A8A'
                                                  : '#6A6A6A'
                                        }
                                        d="M4.76,10.59a1,1,0,0,0,.26-2L3.26,8.15a1,1,0,1,0-.52,1.93l1.76.47A.78.78,0,0,0,4.76,10.59ZM8.62,5a1,1,0,0,0,1,.74.82.82,0,0,0,.26,0,1,1,0,0,0,.7-1.22l-.47-1.76a1,1,0,1,0-1.93.52Zm4.83,10A1,1,0,0,0,12,15L8.5,18.56a2.21,2.21,0,0,1-3.06,0,2.15,2.15,0,0,1,0-3.06L9,12a1,1,0,1,0-1.41-1.41L4,14.08A4.17,4.17,0,1,0,9.92,20l3.53-3.53A1,1,0,0,0,13.45,15ZM5.18,6.59a1,1,0,0,0,.7.29,1,1,0,0,0,.71-.29,1,1,0,0,0,0-1.41L5.3,3.89A1,1,0,0,0,3.89,5.3Zm16.08,7.33-1.76-.47A1,1,0,1,0,19,15.38l1.76.47.26,0a1,1,0,0,0,.26-2ZM15.38,19a1,1,0,0,0-1.23-.7,1,1,0,0,0-.7,1.22l.47,1.76a1,1,0,0,0,1,.74,1.15,1.15,0,0,0,.26,0,1,1,0,0,0,.71-1.23Zm3.44-1.57a1,1,0,0,0-1.41,1.41l1.29,1.29a1,1,0,0,0,1.41,0,1,1,0,0,0,0-1.41ZM21.2,7A4.16,4.16,0,0,0,14.08,4L10.55,7.56A1,1,0,1,0,12,9L15.5,5.44a2.21,2.21,0,0,1,3.06,0,2.15,2.15,0,0,1,0,3.06L15,12a1,1,0,0,0,0,1.41,1,1,0,0,0,1.41,0L20,9.92A4.19,4.19,0,0,0,21.2,7Z"
                                    ></path>
                                </svg>
                                Отвязать
                            </button>
                        </div>
                        <div className="flex flex-1 h-full items-center justify-center text-center"></div>
                        <div className="flex w-8 h-full items-center justify-center text-center"></div>
                        <div className="flex w-8 h-full items-center justify-center text-center"></div>
                        <div className="flex w-8 h-full items-center justify-center text-center"></div>
                    </div>
                </div>
                <div className="flex flex-row text-right justify-end items-center gap-2">
                    {props.isSavingPerformancePay ? (
                        ''
                    ) : (
                        <button
                            className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                            onClick={() => {
                                props.handlePerformancePaymentsAddPopup()
                            }}
                        >
                            Отмена
                        </button>
                    )}

                    <button
                        className={`flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830]  
                            ${
                                isSaveDisabled
                                    ? 'text-[#666666] dark:text-[#888888] bg-[#f6f6f6] dark:bg-[#494849]'
                                    : 'text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'
                            } `}
                        disabled={isSaveDisabled}
                        onClick={() => {
                            if (isSaveDisabled) return
                            handleSave()
                        }}
                    >
                        {props.isSavingPerformancePay ? (
                            <svg
                                className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                ></path>
                            </svg>
                        ) : props.performanceContractPaymentsPosition?.payment
                              ?.guid != null ? (
                            'Сохранить'
                        ) : (
                            'Добавить'
                        )}
                    </button>
                </div>
            </div>

            {deleteRowPerformancePaymentAcceptancePopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#00000050] flex justify-center items-center">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Отвязать документ?
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={
                                        handleDeleteRowPerformanceDocsPositionPopup
                                    }
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                    onClick={() => {
                                        handleDeleteSelected()
                                        setSelectAll(false)
                                        handleDeleteRowPerformanceDocsPositionPopup()
                                    }}
                                >
                                    Отвязать
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {performancePaymentsAcceptancePopup && (
                <PerformancePaymentsAcceptancePopup
                    theme={props.theme}
                    contractGuid={props.contractGuid}
                    handlePerformancePaymentsAcceptancePopup={
                        handlePerformancePaymentsAcceptancePopup
                    }
                    onSave={(newSelectedAcceptances) => {
                        updateAcceptancesWithPaymentSum(newSelectedAcceptances)
                        setSelectedItems([])
                        setSelectAll(false)
                    }}
                />
            )}
        </div>
    )
}
export default PerformancePaymentsAddPopup
